import { Loomduct } from '../core/loomduct'
import type { Plugin, PluginContext } from '../core/types'
import { webAnalytics } from '../web-analytics'
import { findPluginBase, readScriptConfig } from './config'

const stubMethods =
  'load track identify reset use destroy beforeSend onError'.split(' ')

const pluginFiles = new Set(['client-hints', 'web-vitals'])

export type Stub = Record<PropertyKey, unknown>

export function startFromScript(
  script: HTMLScriptElement,
  stub: Stub | undefined,
  version: string,
): Loomduct {
  const settings = readScriptConfig(script)
  const base = findPluginBase(script.src, version)
  const fromFile = (name: string): Plugin => {
    let context: PluginContext | undefined
    let inner: Plugin | undefined
    return {
      name,
      init: (ctx) => {
        context = ctx
      },
      load: async () => {
        const module = (await import(`${base}${name}.js`)) as {
          plugin?: () => Plugin
        }
        inner = module.plugin?.()
        if (inner && context) {
          inner.init?.(context)
        }
        await inner?.load?.()
      },
      beforeSend: (event) =>
        inner?.beforeSend ? inner.beforeSend(event) : event,
      flush: () => inner?.flush?.(),
      destroy: () => inner?.destroy?.(),
    }
  }
  const loomduct: Loomduct = new Loomduct({
    ...settings.config,
    plugins: [
      ...settings.plugins
        .filter((name) => name === 'client-hints')
        .map(fromFile),
      webAnalytics(settings.webAnalytics),
      ...settings.plugins
        .filter((name) => name !== 'client-hints')
        .map(fromFile),
    ],
  })
  const useDirectly = loomduct.use
  Object.assign(loomduct, {
    use: (plugin: Plugin | string) =>
      typeof plugin !== 'string'
        ? useDirectly(plugin)
        : pluginFiles.has(plugin)
          ? useDirectly(fromFile(plugin))
          : undefined,
  })
  const target = loomduct as unknown as Record<
    string,
    (...values: unknown[]) => unknown
  >
  for (const entry of Array.isArray(stub?.q)
    ? (stub.q as [string, unknown[]][])
    : []) {
    if (stubMethods.includes(entry[0])) {
      target[entry[0]]?.(...entry[1])
    }
  }
  for (const method of stubMethods) {
    if (stub) {
      stub[method] = (...args: unknown[]) => target[method]?.(...args)
    }
  }
  if (!settings.manualLoad) {
    void loomduct.load()
  }
  return loomduct
}
